// Iter 24 — Export aggregated shopping list (PDF / Excel / WhatsApp / Print)
//
// Each function receives `{ trip, items }` where:
//   trip  = { destination_country, destination_city, departure_date, return_date, jastiper_name }
//   items = [ { name, total_qty, unit_price, buyers: [{ buyer_name, qty }] }, ... ]
//
// All exports are CLIENT-SIDE — nothing is uploaded.

import { formatIDR } from "./api";

function tripTitle(trip) {
  const city = trip.destination_city ? `${trip.destination_city}, ` : "";
  return `${city}${trip.destination_country || ""}`.trim();
}

function tripDateRange(trip) {
  if (!trip.departure_date) return "";
  return `${trip.departure_date} → ${trip.return_date || ""}`;
}

// -------- PDF (jsPDF + autoTable) --------
export async function exportShoppingPDF({ trip, items }) {
  const { jsPDF } = await import("jspdf");
  const autoTableMod = await import("jspdf-autotable");
  const autoTable = autoTableMod.default || autoTableMod.autoTable;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFontSize(16);
  doc.text("Daftar Belanja Trip", 40, 50);
  doc.setFontSize(11);
  doc.text(`Tujuan: ${tripTitle(trip)}`, 40, 70);
  doc.text(`Tanggal: ${tripDateRange(trip)}`, 40, 86);
  if (trip.jastiper_name) doc.text(`Jastiper: ${trip.jastiper_name}`, 40, 102);

  const head = [["#", "Item", "Qty Total", "Harga Satuan", "Subtotal", "Dari Buyer"]];
  const body = items.map((it, i) => [
    i + 1,
    it.name || "—",
    `${it.total_qty}`,
    formatIDR(it.unit_price || 0),
    formatIDR((it.unit_price || 0) * (it.total_qty || 0)),
    (it.buyers || []).map((b) => `${b.buyer_name} ×${b.qty}`).join(", "),
  ]);

  autoTable(doc, {
    head,
    body,
    startY: 120,
    headStyles: { fillColor: [5, 150, 105] },
    styles: { fontSize: 9, cellPadding: 6 },
    columnStyles: { 5: { cellWidth: 160 } },
  });

  const fname = `daftar-belanja-${tripTitle(trip).replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "trip"}.pdf`;
  doc.save(fname);
}

// -------- Excel (xlsx) --------
export async function exportShoppingExcel({ trip, items }) {
  const XLSX = await import("xlsx");

  const rows = [
    ["Daftar Belanja Trip"],
    ["Tujuan", tripTitle(trip)],
    ["Tanggal", tripDateRange(trip)],
    ["Jastiper", trip.jastiper_name || ""],
    [],
    ["#", "Item", "Qty Total", "Harga Satuan (IDR)", "Subtotal (IDR)", "Dari Buyer"],
    ...items.map((it, i) => [
      i + 1,
      it.name || "—",
      it.total_qty || 0,
      it.unit_price || 0,
      (it.unit_price || 0) * (it.total_qty || 0),
      (it.buyers || []).map((b) => `${b.buyer_name} ×${b.qty}`).join(", "),
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 4 }, { wch: 28 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 40 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Daftar Belanja");
  const fname = `daftar-belanja-${tripTitle(trip).replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "trip"}.xlsx`;
  XLSX.writeFile(wb, fname);
}

// -------- WhatsApp (text share) --------
export function exportShoppingWhatsApp({ trip, items }) {
  const lines = [];
  lines.push(`*Daftar Belanja Trip*`);
  lines.push(`Tujuan: ${tripTitle(trip)}`);
  lines.push(`Tanggal: ${tripDateRange(trip)}`);
  if (trip.jastiper_name) lines.push(`Jastiper: ${trip.jastiper_name}`);
  lines.push("");
  items.forEach((it, i) => {
    lines.push(`${i + 1}. *${it.name}* — ×${it.total_qty}`);
    if (it.buyers && it.buyers.length) {
      lines.push(`   _(${it.buyers.map((b) => `${b.buyer_name} ×${b.qty}`).join(", ")})_`);
    }
  });
  lines.push("");
  lines.push("— Dikirim via Jastip-Safe");
  const text = encodeURIComponent(lines.join("\n"));
  window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
}

// -------- Print (window.print with isolated printable view) --------
export function exportShoppingPrint({ trip, items }) {
  const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=900");
  if (!w) {
    alert("Browser memblokir popup. Izinkan popup untuk mencetak.");
    return;
  }
  const rows = items.map((it, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${escapeHtml(it.name || "—")}</strong></td>
      <td class="num">×${it.total_qty}</td>
      <td class="num">${formatIDR(it.unit_price || 0)}</td>
      <td class="num">${formatIDR((it.unit_price || 0) * (it.total_qty || 0))}</td>
      <td class="small">${(it.buyers || []).map((b) => `${escapeHtml(b.buyer_name)} ×${b.qty}`).join(", ")}</td>
    </tr>
  `).join("");

  w.document.write(`
    <!doctype html><html lang="id"><head><meta charset="utf-8"/>
      <title>Daftar Belanja — ${escapeHtml(tripTitle(trip))}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 32px; color: #111; }
        h1 { font-size: 22px; margin: 0 0 6px; }
        .meta { color: #555; font-size: 13px; margin-bottom: 16px; }
        .meta div { margin: 2px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #ddd; vertical-align: top; }
        th { background: #059669; color: #fff; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; }
        .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .small { font-size: 10px; color: #666; }
        .footer { margin-top: 24px; font-size: 10px; color: #888; }
        @media print { body { padding: 16px; } .noprint { display: none; } }
      </style>
    </head><body>
      <h1>Daftar Belanja Trip</h1>
      <div class="meta">
        <div><strong>Tujuan:</strong> ${escapeHtml(tripTitle(trip))}</div>
        <div><strong>Tanggal:</strong> ${escapeHtml(tripDateRange(trip))}</div>
        ${trip.jastiper_name ? `<div><strong>Jastiper:</strong> ${escapeHtml(trip.jastiper_name)}</div>` : ""}
      </div>
      <table>
        <thead><tr><th>#</th><th>Item</th><th class="num">Qty</th><th class="num">Harga Satuan</th><th class="num">Subtotal</th><th>Dari Buyer</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">Dicetak via Jastip-Safe · ${new Date().toLocaleString("id-ID")}</div>
      <script>window.onload = () => { setTimeout(() => window.print(), 200); };<\/script>
    </body></html>
  `);
  w.document.close();
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}
